/**
 * Summary Generator Utility
 * 
 * Generates a concise summary of requirements changes by comparing
 * before and after states. Uses intelligent analysis to create summaries.
 */

import { Requirements } from '../../../../../Entities/JobDescription';

/**
 * Calculate similarity between two requirement descriptions
 */
const calculateSimilarity = (desc1: string, desc2: string): number => {
  const normalize = (str: string) => str.toLowerCase().trim().replace(/\s+/g, ' ');
  const norm1 = normalize(desc1);
  const norm2 = normalize(desc2);

  if (norm1 === norm2) return 1.0;
  if (norm1.includes(norm2) || norm2.includes(norm1)) return 0.8;

  const words1 = new Set(norm1.split(' '));
  const words2 = new Set(norm2.split(' '));
  const words1Array = Array.from(words1);
  const words2Array = Array.from(words2);
  const intersection = new Set(words1Array.filter((x) => words2.has(x)));
  const union = new Set([...words1Array, ...words2Array]);
  
  return union.size === 0 ? 0 : intersection.size / union.size;
};

/**
 * Generate a summary of requirements changes using AI
 * 
 * @param requirementsBefore - Requirements before refinement
 * @param requirementsAfter - Requirements after refinement
 * @param prompt - The refinement prompt that was used
 * @param action - 'refine' or 'generate'
 * @returns Summary string describing the changes
 */
export const generateRequirementsSummary = async (
  requirementsBefore: Requirements[],
  requirementsAfter: Requirements[],
  prompt: string,
  action: 'refine' | 'generate'
): Promise<string> => {
  // For Phase 1, use intelligent analysis to generate summaries
  // In future phases, this can call a dedicated AI summary endpoint
  // The intelligent analysis compares requirements and identifies changes
  return generateIntelligentSummary(requirementsBefore, requirementsAfter, prompt, action);
};


/**
 * Generate an intelligent summary by analyzing changes
 */
const generateIntelligentSummary = (
  requirementsBefore: Requirements[],
  requirementsAfter: Requirements[],
  prompt: string,
  action: 'refine' | 'generate'
): string => {
  if (action === 'generate') {
    const mustMatchCount = requirementsAfter.filter(r => r.mustMatch).length;
    return `Generated ${requirementsAfter.length} initial requirements${mustMatchCount > 0 ? `, with ${mustMatchCount} marked as must-match` : ''}.`;
  }

  // Analyze changes for refinement
  const beforeCount = requirementsBefore.length;
  const afterCount = requirementsAfter.length;
  const difference = afterCount - beforeCount;

  // Find added requirements (in after but not in before)
  const added: Requirements[] = [];
  const modified: Requirements[] = [];
  const removed: Requirements[] = [];

  requirementsAfter.forEach(afterReq => {
    const bestMatch = requirementsBefore.find(beforeReq => 
      calculateSimilarity(afterReq.description, beforeReq.description) > 0.7
    );
    if (!bestMatch) {
      added.push(afterReq);
    } else if (calculateSimilarity(afterReq.description, bestMatch.description) < 0.95) {
      modified.push(afterReq);
    }
  });

  requirementsBefore.forEach(beforeReq => {
    const hasMatch = requirementsAfter.some(afterReq =>
      calculateSimilarity(afterReq.description, beforeReq.description) > 0.7
    );
    if (!hasMatch) {
      removed.push(beforeReq);
    }
  });

  // Build summary based on analysis
  const parts: string[] = [];
  
  if (added.length > 0) {
    parts.push(`Added ${added.length} new requirement${added.length > 1 ? 's' : ''}`);
  }
  if (modified.length > 0) {
    parts.push(`Modified ${modified.length} existing requirement${modified.length > 1 ? 's' : ''}`);
  }
  if (removed.length > 0) {
    parts.push(`Removed ${removed.length} requirement${removed.length > 1 ? 's' : ''}`);
  }

  if (parts.length === 0) {
    return `Refined requirements: Updated ${afterCount} requirement${afterCount > 1 ? 's' : ''} based on feedback.`;
  }

  const summary = `Refined requirements: ${parts.join(', ')}. ${prompt.length > 60 ? prompt.substring(0, 60) + '...' : prompt}`;
  return summary;
};

